import {useCallback, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from 'react-query';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {CreateCheckinCycleFormData} from './types';
import PageLayoutTemplate from '@ui/templates/page-template/page-layout';
import {PreviewCadenceCard} from './preview-cadence-card';
import {PreviewContentCard} from './preview-content-card';
import {PreviewParticipantsCard} from './preview-participants-card';
import {CycleEditConfirmationModal} from './cycle-edit-confirmation-modal';
import {WizardSteps} from './wizard-steps';
import {buildContentFromBlocks} from './content-payload';
import {buildPayload} from './cycle-payload';
import {ReviewActions} from './review-actions';
import {formatCadenceDetails} from './cadence-preview';

export {buildContentFromBlocks} from './content-payload';
export {buildPayload} from './cycle-payload';
export {formatCadenceDetails} from './cadence-preview';

const WIZARD_STEPS = ['Basics', 'Content', 'Cadence', 'Review'];
const previewPageBodyStyle = {
  padding: 0,
  margin: 0,
  backgroundColor: 'var(--beam-canvas)',
};

export type PreviewSection =
  | 'participants'
  | 'visibility'
  | 'content'
  | 'cadence';

interface PreviewStepProps {
  onClose?: () => void;
  onComplete?: () => void;
  onBack?: () => void;
  data: CreateCheckinCycleFormData;
  mode?: 'create' | 'edit';
  cycleId?: string;
  statusOverride?: string;
  createdByName?: string;
  cycleUpdatedAt?: string;
  onEditSection?: (section: PreviewSection) => void;
  hasChanges?: boolean;
  showWizardProgress?: boolean;
  onWizardStepChange?: (step: number) => void;
}

//  https://staging-api.getbeam.co | Docs:  https://staging-api.getbeam.co/v1/docs

export const PreviewStep = ({
  onClose,
  onComplete,
  onBack,
  data,
  mode = 'create',
  cycleId,
  statusOverride,
  onEditSection,
  hasChanges = true,
  showWizardProgress = false,
  onWizardStepChange,
}: PreviewStepProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const isSaveInFlight = useRef(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState<
    'draft' | 'active' | null
  >(null);
  const checkinsRequest = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const checkinsController = useMemo(
    () => new CompanyCheckinsController(checkinsRequest),
    [checkinsRequest],
  );
  const contentData = useMemo(
    () => buildContentFromBlocks(data.contentBlocks),
    [data.contentBlocks],
  );
  const cadenceDetails = useMemo(() => formatCadenceDetails(data.cadence), [
    data.cadence,
  ]);
  const includedTags = useMemo(() => {
    if (data.includeScope.includes('everyone')) {
      return ['Everyone'];
    }
    const tags: string[] = [];
    if (data.includeScope.includes('groups')) {
      tags.push(...data.includeGroups.map((item) => item.label));
    }
    if (data.includeScope.includes('individuals')) {
      tags.push(...data.includePeople.map((item) => item.label));
    }
    return tags;
  }, [data.includeScope, data.includeGroups, data.includePeople]);

  const excludedTags = useMemo(() => {
    const tags: string[] = [];
    if (data.excludeScope.includes('groups')) {
      tags.push(...data.excludeGroups.map((item) => item.label));
    }
    if (data.excludeScope.includes('individuals')) {
      tags.push(...data.excludePeople.map((item) => item.label));
    }
    return tags;
  }, [data.excludeScope, data.excludeGroups, data.excludePeople]);

  const cycleTitle = data.cycleName?.trim() || '';

  const isEditMode = mode === 'edit' && Boolean(cycleId);

  // Check if the cycle is in a read-only state (ended or archived)
  const isReadOnlyStatus =
    statusOverride === 'ended' || statusOverride === 'archived';
  const isActiveOrPaused =
    statusOverride === 'active' || statusOverride === 'paused';
  const requiresConfirmation = isEditMode && isActiveOrPaused;

  const handleCreateCycle = useCallback(
    async (status: 'draft' | 'active') => {
      if (isSaveInFlight.current) {
        return;
      }

      isSaveInFlight.current = true;
      status === 'draft' ? setIsSavingDraft(true) : setIsPublishing(true);
      let didCreateCycle = false;

      try {
        const payload = buildPayload(data, status, contentData);
        const response = await checkinsController.createCheckinCycle(payload);
        didCreateCycle = response !== false;

        if (didCreateCycle) {
          void queryClient.invalidateQueries(['check-in-cycles']);
        }
      } finally {
        isSaveInFlight.current = false;
        status === 'draft' ? setIsSavingDraft(false) : setIsPublishing(false);
      }

      if (didCreateCycle) {
        onComplete
          ? onComplete()
          : navigate('/checkins/team-management', {replace: true});
      }
    },
    [checkinsController, contentData, data, navigate, onComplete, queryClient],
  );

  const handleUpdateCycle = useCallback(
    async (status?: 'draft' | 'active') => {
      if (!cycleId || isSaveInFlight.current) {
        return;
      }

      isSaveInFlight.current = true;
      const targetStatus = status || 'active';
      targetStatus === 'draft' ? setIsSavingDraft(true) : setIsPublishing(true);
      let didUpdateCycle = false;

      try {
        const payload = buildPayload(data, targetStatus, contentData);
        const response = await checkinsController.updateCheckinCycle(
          cycleId,
          payload,
        );
        didUpdateCycle = response !== false;

        if (didUpdateCycle) {
          void Promise.all([
            queryClient.invalidateQueries(['check-in-cycles']),
            queryClient.invalidateQueries(['check-in-cycle', cycleId]),
          ]);
        }
      } finally {
        isSaveInFlight.current = false;
        targetStatus === 'draft'
          ? setIsSavingDraft(false)
          : setIsPublishing(false);
      }

      if (didUpdateCycle) {
        onComplete
          ? onComplete()
          : navigate('/checkins/team-management', {replace: true});
      }
    },
    [
      cycleId,
      contentData,
      data,
      checkinsController,
      navigate,
      onComplete,
      queryClient,
    ],
  );

  // Handle save with confirmation for active/paused cycles
  const handleSaveWithConfirmation = useCallback(
    (status: 'draft' | 'active') => {
      if (requiresConfirmation) {
        setPendingSaveAction(status);
        setShowConfirmationModal(true);
      } else {
        if (isEditMode) {
          handleUpdateCycle(status);
        } else {
          handleCreateCycle(status);
        }
      }
    },
    [requiresConfirmation, isEditMode, handleUpdateCycle, handleCreateCycle],
  );

  // Handle modal confirmation
  const handleConfirmSave = useCallback(() => {
    setShowConfirmationModal(false);
    if (pendingSaveAction && isEditMode) {
      handleUpdateCycle(pendingSaveAction);
    }
    setPendingSaveAction(null);
  }, [pendingSaveAction, isEditMode, handleUpdateCycle]);

  // Handle modal cancellation
  const handleCancelSave = useCallback(() => {
    setShowConfirmationModal(false);
    setPendingSaveAction(null);
  }, []);

  return (
    <PageLayoutTemplate
      title={
        cycleTitle ||
        (isEditMode ? 'Edit Check-in Cycle' : 'Create Check-in Cycle')
      }
      onClick={() => onClose?.()}
      bodyStyle={previewPageBodyStyle}>
      {showWizardProgress && (
        <WizardSteps
          steps={WIZARD_STEPS}
          activeStep={4}
          onChange={onWizardStepChange}
        />
      )}
      <main className="mx-auto flex w-full max-w-[559px] flex-col gap-4 px-4 pb-12 pt-10">
        <PreviewParticipantsCard
          cycleName={cycleTitle}
          includedTags={includedTags}
          excludedTags={excludedTags}
          onEdit={
            onEditSection ? () => onEditSection('participants') : undefined
          }
        />
        <PreviewContentCard
          blocks={data.contentBlocks}
          visibility={data.visibility}
          onEdit={onEditSection ? () => onEditSection('content') : undefined}
        />
        <PreviewCadenceCard
          details={cadenceDetails}
          onEdit={onEditSection ? () => onEditSection('cadence') : undefined}
        />

        <ReviewActions
          onBack={onBack || onClose}
          onSaveDraft={
            !isReadOnlyStatus && !isActiveOrPaused
              ? isEditMode
                ? () => handleSaveWithConfirmation('draft')
                : () => handleCreateCycle('draft')
              : undefined
          }
          onPrimary={
            !isReadOnlyStatus
              ? isEditMode
                ? () => handleSaveWithConfirmation('active')
                : () => handleCreateCycle('active')
              : undefined
          }
          saveDraftLabel={isEditMode ? 'Update as draft' : 'Save as draft'}
          primaryLabel={
            isActiveOrPaused
              ? 'Save'
              : isEditMode
              ? 'Publish'
              : 'Launch check-in'
          }
          primaryLoadingLabel={
            isActiveOrPaused
              ? 'Saving...'
              : isEditMode
              ? 'Publishing...'
              : 'Launching'
          }
          isSavingDraft={isSavingDraft}
          isSavingPrimary={isPublishing}
          primaryDisabled={isEditMode && !hasChanges}
        />
      </main>

      <CycleEditConfirmationModal
        isOpen={showConfirmationModal}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        isLoading={isSavingDraft || isPublishing}
        cycleStatus={(statusOverride as 'active' | 'paused') || 'active'}
      />
    </PageLayoutTemplate>
  );
};
